import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  RefreshCw,
  Server,
  Plug,
  PlugZap,
  PowerOff,
  RotateCw,
  Pencil,
  Trash2,
  ShieldCheck,
  Globe,
  Key,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  AlertTriangle,
  Loader2,
  Search,
  Settings,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { useToast } from "../../../../hooks/use-toast";
import {
  GatewayServerConfigApi,
  GatewayAuthConfigApi,
} from "../Api/gatewayApi";
import { McpServerConfig, AuthConfig } from "../interfaces/gatewayInterfaces";
import {
  formatTimestamp,
  timeAgo,
  detectIdpType,
  apiError,
  GRACE_PERIOD_OPTIONS,
  TOKEN_CLASSIFICATION_OPTIONS,
} from "../utils/gatewayUtils";
import { Button } from "../../../ui/button";
import { Badge } from "../../../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../ui/tabs";


type TransportType = "STDIO" | "SSE" | "STREAMABLE_HTTP";

interface ServerFormState {
  name: string;
  transportType: TransportType;
  command: string;
  args: string;
  url: string;
  env: string;
  timeoutSeconds: number;
  enabled: boolean;
  autoConnect: boolean;
  headers: string;
  serverConfig: string;
}

const EMPTY_SERVER_FORM: ServerFormState = {
  name: "",
  transportType: "STREAMABLE_HTTP",
  command: "",
  args: "",
  url: "",
  env: "",
  timeoutSeconds: 30,
  enabled: true,
  autoConnect: true,
  headers: "",
  serverConfig: "",
};

interface AuthFormState {
  idpDisplayName: string;
  authMode: string;
  issuerUri: string;
  jwksUri: string;
  authorizationEndpoint: string;
  tokenEndpoint: string;
  tokenClassificationMode: string;
  gracePeriodMinutes: string;
}

const EMPTY_AUTH_FORM: AuthFormState = {
  idpDisplayName: "",
  authMode: "oauth2",
  issuerUri: "",
  jwksUri: "",
  authorizationEndpoint: "",
  tokenEndpoint: "",
  tokenClassificationMode: "STRICT",
  gracePeriodMinutes: "30",
};

interface ValidationResult {
  state: "idle" | "loading" | "success" | "error";
  message?: string;
  jwksKeyCount?: number;
  jwksLatencyMs?: number;
}


function idpIcon(issuerUri: string | undefined): React.ReactNode {
  const name = detectIdpType(issuerUri);
  switch (name) {
    case "Keycloak":
      return <Key className="w-4 h-4" />;
    case "Azure AD":
      return <ShieldCheck className="w-4 h-4" />;
    case "Okta":
    case "Auth0":
      return <ShieldCheck className="w-4 h-4" />;
    case "Google":
      return <Globe className="w-4 h-4" />;
    default:
      return <ShieldCheck className="w-4 h-4" />;
  }
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function parseEnvJson(raw: string): Record<string, string> | undefined {
  if (!raw.trim()) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function parseArgs(raw: string): string[] {
  if (!raw.trim()) return [];
  return raw.split(/\s+/).filter(Boolean);
}


interface ServerCardProps {
  server: McpServerConfig;
  onEdit: (server: McpServerConfig) => void;
  onDelete: (server: McpServerConfig) => void;
  onConnect: (name: string) => void;
  onDisconnect: (name: string) => void;
  onReconnect: (name: string) => void;
  actionLoading: string | null;
}

function ServerCard({
  server,
  onEdit,
  onDelete,
  onConnect,
  onDisconnect,
  onReconnect,
  actionLoading,
}: ServerCardProps) {
  const { toast } = useToast();
  const isConnected = server.connected === true;
  const isEnabled = server.enabled !== false;
  const name = server.name;
  const isBusy = actionLoading === name;
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const protocolType = (server.type || server.transportType || "HTTP").toUpperCase();
  const lastPulse = server.lastActivity ? timeAgo(server.lastActivity) : "No activity";
  const headers =
    server.headers && Object.keys(server.headers).length > 0
      ? server.headers
      : null;

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <h3 className="text-2xl font-extrabold text-foreground tracking-tight">{name}</h3>
          {!isEnabled && (
            <Badge variant="outline" className="text-[10px] border-border text-muted-foreground bg-muted/60">
              Disabled
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(server)} disabled={isBusy} className="border-border text-foreground/80 hover:text-foreground h-8 w-8 p-0" title="Edit server">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          {isConnected ? (
            <>
              <Button variant="outline" size="sm" onClick={() => { setActiveAction("reconnect"); onReconnect(name); }} disabled={isBusy} className="border-primary/40 text-primary hover:bg-primary/10 h-8 w-8 p-0" title="Reconnect">
                {isBusy && activeAction === "reconnect" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="outline" size="sm" onClick={() => { setActiveAction("disconnect"); onDisconnect(name); }} disabled={isBusy} className="border-amber-700/40 text-amber-400 hover:bg-amber-500/10 h-8 w-8 p-0" title="Disconnect">
                {isBusy && activeAction === "disconnect" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PowerOff className="w-3.5 h-3.5" />}
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={() => { setActiveAction("connect"); onConnect(name); }} disabled={isBusy || !isEnabled} className="border-emerald-700/40 text-emerald-400 hover:bg-emerald-500/10 h-8 w-8 p-0" title="Connect">
              {isBusy && activeAction === "connect" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onDelete(server)} disabled={isBusy} className="border-red-800/30 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" title="Delete server">
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 p-6">
        <div className="flex flex-col gap-3">
          <div className={cn(
            "inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full text-xs font-medium border",
            isConnected
              ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
              : "bg-red-500/10 text-red-400 border-red-500/20"
          )}>
            <span className={cn(
              "w-2 h-2 rounded-full",
              isConnected ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" : "bg-red-400"
            )} />
            {isConnected ? "Connected" : "Disconnected"}
          </div>
          {isConnected && server.connectedAt && (
            <p className="text-[11px] text-muted-foreground">
              Since {formatTimestamp(server.connectedAt)}
            </p>
          )}

          <div className="grid grid-cols-3 xl:grid-cols-1 gap-2">
            {[
              { label: "Tools", value: server.toolCount || 0 },
              { label: "Resources", value: server.resourceCount || 0 },
              { label: "Prompts", value: server.promptCount || 0 },
            ].map((cap) => (
              <div key={cap.label} className="flex flex-col items-center justify-center xl:flex-row xl:items-center xl:justify-between rounded-lg border border-border bg-muted/60 px-2 py-3 text-center xl:text-left">
                <span className="text-[9px] text-muted-foreground uppercase tracking-wider mb-1 xl:mb-0">{cap.label}</span>
                <span className="text-lg font-bold text-foreground leading-none">{cap.value}</span>
              </div>
            ))}
          </div>

        </div>

        <div className="xl:col-span-2 flex flex-col gap-4">
          <div className="rounded-xl border border-border bg-muted/60 p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-5">
              Network Configuration
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Protocol Type</p>
                <span className="text-sm font-bold text-foreground">{protocolType}</span>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Auto-Connect</p>
                <span className={cn("text-sm font-semibold", server.autoConnect !== false ? "text-emerald-400" : "text-muted-foreground")}>
                  {server.autoConnect !== false ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>

            <div className="mb-5">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Endpoint URL</p>
              <div className="flex items-start gap-2 font-mono text-xs text-foreground/70 bg-muted/60 rounded-lg px-4 py-3 border border-border">
                <span className="flex-1 break-all leading-6">{server.url || server.command || "-"}</span>
                {(server.url || server.command) && (
                  <button
                    onClick={async () => {
                      await copyToClipboard(server.url || server.command || "");
                      toast({ title: "Copied to clipboard" });
                    }}
                    className="text-muted-foreground/50 hover:text-foreground flex-shrink-0 transition-colors mt-0.5"
                    title="Copy"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Timeout</p>
                <span className="text-sm font-bold text-foreground">{server.timeoutSeconds || 30}s</span>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Last Pulse</p>
                <span className="text-sm text-foreground/60 italic">{lastPulse}</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {(headers || server.serverConfig) && (
        <div className="px-6 pb-6 -mt-2">
          <div className="rounded-xl border border-border bg-muted/60 p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
              Authorization Security
            </p>

            <div className={cn("grid gap-4", headers && server.serverConfig ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
              {headers && (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Custom Headers</p>
                  <div className="bg-muted/60 rounded-lg border border-border p-4 font-mono text-xs space-y-1.5">
                    {Object.entries(headers).map(([k, v]) => {
                      const val = String(v);
                      const redacted = val.length > 20
                        ? val.substring(0, 4) + "***REDACTED***" + val.substring(val.length - 4)
                        : val;
                      return (
                        <p key={k} className="text-foreground/60 break-all leading-5">
                          <span className="text-muted-foreground">{k}:</span>{" "}
                          <span className="text-amber-400/80">{redacted}</span>
                        </p>
                      );
                    })}
                  </div>
                </div>
              )}

              {server.serverConfig && (
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Server Config (JSON)</p>
                  <div className="bg-muted/60 rounded-lg border border-border p-4 font-mono text-xs text-foreground/60 break-all leading-5 max-h-[200px] overflow-y-auto">
                    {typeof server.serverConfig === "string"
                      ? server.serverConfig
                      : JSON.stringify(server.serverConfig, null, 2)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


interface ServerFormDialogProps {
  open: boolean;
  editingServer: McpServerConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

function ServerFormDialog({ open, editingServer, onClose, onSaved }: ServerFormDialogProps) {
  const { toast } = useToast();
  const [form, setForm] = useState<ServerFormState>(EMPTY_SERVER_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (editingServer) {
        const s = editingServer as any;
        setForm({
          name: s.serverName || s.name || "",
          transportType: (() => {
            const raw = (s.transportType || s.type || "").toUpperCase();
            if (raw === "STDIO") return "STDIO";
            if (raw === "SSE") return "SSE";
            return "STREAMABLE_HTTP";
          })() as TransportType,
          command: s.command ?? "",
          args: s.args?.join?.(" ") ?? "",
          url: s.url ?? "",
          env: s.env ? JSON.stringify(s.env, null, 2) : "",
          timeoutSeconds: s.timeoutSeconds ?? 30,
          enabled: s.enabled !== false,
          autoConnect: s.autoConnect !== false,
          headers: s.headers && Object.keys(s.headers).length > 0 ? JSON.stringify(s.headers, null, 2) : "",
          serverConfig: s.serverConfig ? JSON.stringify(s.serverConfig, null, 2) : "",
        });
      } else {
        setForm(EMPTY_SERVER_FORM);
      }
    }
  }, [open, editingServer]);

  const isStdio = form.transportType === "STDIO";
  const isEdit = editingServer !== null;

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: "Server name is required", variant: "destructive" });
      return;
    }
    if (isStdio && !form.command.trim()) {
      toast({ title: "Command is required for STDIO transport", variant: "destructive" });
      return;
    }
    if (!isStdio && !form.url.trim()) {
      toast({ title: "URL is required for this transport type", variant: "destructive" });
      return;
    }
    if (!isStdio && !/^https?:\/\//.test(form.url.trim())) {
      toast({ title: "URL must start with http:// or https://", variant: "destructive" });
      return;
    }

    let parsedEnv: Record<string, string> | undefined;
    if (form.env.trim()) {
      parsedEnv = parseEnvJson(form.env);
      if (!parsedEnv) {
        toast({ title: "Environment variables JSON is invalid", variant: "destructive" });
        return;
      }
    }

    let headers: Record<string, string> = {};
    if (form.headers.trim()) {
      try { headers = JSON.parse(form.headers); }
      catch { toast({ title: "Headers JSON is invalid", variant: "destructive" }); return; }
    }

    let serverConfig: any = null;
    if (form.serverConfig.trim()) {
      try { serverConfig = JSON.parse(form.serverConfig); }
      catch { toast({ title: "Server Config JSON is invalid", variant: "destructive" }); return; }
    }

    const body: any = {
      serverName: form.name.trim(),
      type: form.transportType === "STDIO" ? "stdio" : "http",
      ...(isStdio
        ? { command: form.command.trim(), args: parseArgs(form.args) }
        : { url: form.url.trim() }),
      headers,
      ...(serverConfig ? { serverConfig } : {}),
      timeoutSeconds: form.timeoutSeconds,
      enabled: form.enabled,
      autoConnect: form.autoConnect,
      ...(parsedEnv ? { env: parsedEnv } : {}),
    };

    setSaving(true);
    try {
      if (isEdit) {
        await GatewayServerConfigApi.update(editingServer!.name, body);
        toast({ title: `Server '${form.name}' updated` });
      } else {
        await GatewayServerConfigApi.create(body);
        toast({ title: `Server '${form.name}' created` });
      }
      onSaved();
      onClose();
    } catch (e: any) {
      toast({
        title: "Save failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50";
  const labelCls = "text-xs font-medium text-muted-foreground uppercase tracking-wider";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-card border-border text-foreground max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {isEdit ? "Edit Server Configuration" : "Add MCP Server"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {isEdit
              ? "Update the connection parameters for this MCP server."
              : "Configure a new MCP server connection for the gateway."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-6 py-2">
          <section>
            <div className="flex items-center gap-2 mb-3 text-sm font-medium text-foreground/80">
              <Plug className="w-4 h-4 text-primary" />
              General Connection
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>
                  Server Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  disabled={isEdit}
                  placeholder="e.g. github"
                  className={cn(inputCls, isEdit && "opacity-60 cursor-not-allowed")}
                />
                <span className="text-[10px] text-muted-foreground/70">Alphanumeric, underscore, or hyphen only</span>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Type</label>
                <Select
                  value={form.transportType}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, transportType: v as TransportType }))
                  }
                >
                  <SelectTrigger className="bg-muted border-border text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-muted border-border">
                    <SelectItem value="STREAMABLE_HTTP">HTTP</SelectItem>
                    <SelectItem value="SSE">SSE</SelectItem>
                    <SelectItem value="STDIO">STDIO</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {isStdio && (
                <>
                  <div className="flex flex-col gap-1.5 col-span-2">
                    <label className={labelCls}>
                      Command <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={form.command}
                      onChange={(e) => setForm((f) => ({ ...f, command: e.target.value }))}
                      placeholder="e.g. npx"
                      className={inputCls}
                    />
                  </div>
                  <div className="flex flex-col gap-1.5 col-span-2">
                    <label className={labelCls}>
                      Args
                      <span className="text-muted-foreground/70 ml-1 font-normal normal-case">(space-separated)</span>
                    </label>
                    <input
                      type="text"
                      value={form.args}
                      onChange={(e) => setForm((f) => ({ ...f, args: e.target.value }))}
                      placeholder="-y @modelcontextprotocol/server-github"
                      className={inputCls}
                    />
                  </div>
                </>
              )}

              {!isStdio && (
                <div className="flex flex-col gap-1.5 col-span-2">
                  <label className={labelCls}>
                    Target URL <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.url}
                    onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                    placeholder="https://api.example.com/mcp/"
                    className={inputCls}
                  />
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>Timeout (seconds)</label>
                <input
                  type="number"
                  value={form.timeoutSeconds}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      timeoutSeconds: Math.max(5, Math.min(300, Number(e.target.value) || 30)),
                    }))
                  }
                  min={5}
                  max={300}
                  className={inputCls}
                />
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3 text-sm font-medium text-foreground/80">
              <ShieldCheck className="w-4 h-4 text-primary" />
              Execution Policy
            </div>
            <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/60 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-foreground">Server Enabled</p>
                  <p className="text-xs text-muted-foreground">Allow incoming traffic</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.enabled}
                  onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
                  className={cn(
                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                    form.enabled ? "bg-primary" : "bg-muted-foreground/50"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                      form.enabled ? "translate-x-[18px]" : "translate-x-[3px]"
                    )}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-foreground">Auto-connect</p>
                  <p className="text-xs text-muted-foreground">Initialize on startup</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.autoConnect}
                  onClick={() => setForm((f) => ({ ...f, autoConnect: !f.autoConnect }))}
                  className={cn(
                    "relative inline-flex h-5 w-9 items-center rounded-full transition-colors",
                    form.autoConnect ? "bg-primary" : "bg-muted-foreground/50"
                  )}
                >
                  <span
                    className={cn(
                      "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform",
                      form.autoConnect ? "translate-x-[18px]" : "translate-x-[3px]"
                    )}
                  />
                </button>
              </div>
            </div>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3 text-sm font-medium text-foreground/80">
              <Server className="w-4 h-4 text-primary" />
              Data Payload Configuration
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>
                  Custom Headers (JSON)
                  <span className="text-muted-foreground/70 ml-1 font-normal normal-case">
                    — use <code className="text-primary/70 text-[10px]">{"${env:VAR_NAME}"}</code> for secrets
                  </span>
                </label>
                <textarea
                  value={form.headers}
                  onChange={(e) => setForm((f) => ({ ...f, headers: e.target.value }))}
                  rows={4}
                  placeholder={'{\n  "Authorization": "Bearer ${env:GITHUB_TOKEN}"\n}'}
                  className={cn(inputCls, "font-mono resize-none")}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className={labelCls}>
                  Server Config Schema (JSON, optional)
                </label>
                <textarea
                  value={form.serverConfig}
                  onChange={(e) => setForm((f) => ({ ...f, serverConfig: e.target.value }))}
                  rows={3}
                  placeholder={'{\n  "jiraUrl": "https://company.atlassian.net"\n}'}
                  className={cn(inputCls, "font-mono resize-none")}
                />
              </div>
            </div>
          </section>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
            className="text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary text-white"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? "Update Server" : "Add Server"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


interface DeleteConfirmDialogProps {
  open: boolean;
  serverName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

function DeleteConfirmDialog({
  open,
  serverName,
  onClose,
  onConfirm,
  loading,
}: DeleteConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm bg-card border-border text-foreground">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <Trash2 className="w-5 h-5" />
            Delete Server
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Are you sure you want to delete{" "}
            <span className="font-semibold text-foreground">{serverName}</span>? This action cannot
            be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
            className="text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className="bg-red-700 hover:bg-red-600 text-white"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function priorityFilter<T>(items: T[], query: string, getName: (item: T) => string): T[] {
  if (!query.trim()) return items;
  const q = query.toLowerCase().trim();
  return items.filter((item) => getName(item).toLowerCase().startsWith(q));
}

function McpServersTab() {
  const { toast } = useToast();
  const [servers, setServers] = useState<McpServerConfig[]>([]);
  const [loadingServers, setLoadingServers] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<McpServerConfig | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<McpServerConfig | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadServers = useCallback(async () => {
    setLoadingServers(true);
    try {
      const result = await GatewayServerConfigApi.list();
      setServers(Array.isArray(result) ? result : []);
    } catch (e: any) {
      toast({
        title: "Failed to load server configurations",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setLoadingServers(false);
    }
  }, [toast]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  const handleEdit = (server: McpServerConfig) => {
    setEditingServer(server);
    setFormOpen(true);
  };

  const handleAdd = () => {
    setEditingServer(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingServer(null);
  };

  const handleDeleteRequest = (server: McpServerConfig) => {
    setDeleteTarget(server);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await GatewayServerConfigApi.remove(deleteTarget.name);
      toast({ title: `Server '${deleteTarget.name}' deleted` });
      setDeleteTarget(null);
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Delete failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleConnect = async (name: string) => {
    setActionLoading(name);
    try {
      await GatewayServerConfigApi.connect(name);
      toast({ title: `Server '${name}' connected` });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Connect failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleDisconnect = async (name: string) => {
    setActionLoading(name);
    try {
      await GatewayServerConfigApi.disconnect(name);
      toast({ title: `Server '${name}' disconnected` });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Disconnect failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleReconnect = async (name: string) => {
    setActionLoading(name);
    try {
      await GatewayServerConfigApi.reconnect(name);
      toast({ title: `Server '${name}' reconnected` });
      await loadServers();
    } catch (e: any) {
      toast({
        title: "Reconnect failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const filteredServers = priorityFilter(servers, search, (s) => s.name || "");

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-muted-foreground">
          {servers.length > 0 &&
            `${servers.filter((s) => s.connected).length} of ${servers.length} servers connected`}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadServers}
            className="border-border text-muted-foreground hover:text-foreground"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            size="sm"
            onClick={handleAdd}
            className="bg-primary hover:bg-primary text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Server
          </Button>
        </div>
      </div>

      {servers.length > 0 && (
        <div className="relative w-72 my-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search servers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-muted/60 border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-primary/50 transition-colors"
          />
        </div>
      )}

      {loadingServers ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-52 rounded-xl" />
          ))}
        </div>
      ) : servers.length === 0 ? (
        <div className="rounded-xl border border-border bg-card shadow-sm py-16 text-center flex flex-col items-center gap-3">
          <Server className="w-12 h-12 text-muted-foreground/70" />
          <p className="text-muted-foreground font-medium">No server configurations</p>
          <p className="text-muted-foreground/70 text-sm">
            Click &ldquo;Add Server&rdquo; to configure your first MCP server
          </p>
          <Button
            size="sm"
            onClick={handleAdd}
            className="mt-2 bg-primary hover:bg-primary text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Server
          </Button>
        </div>
      ) : filteredServers.length === 0 ? (
        <div className="rounded-xl border border-border bg-card shadow-sm py-16 text-center text-sm text-muted-foreground">
          No servers match &ldquo;{search}&rdquo;
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredServers.map((server) => (
            <ServerCard
              key={server.name}
              server={server}
              onEdit={handleEdit}
              onDelete={handleDeleteRequest}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              onReconnect={handleReconnect}
              actionLoading={actionLoading}
            />
          ))}
        </div>
      )}

      <ServerFormDialog
        open={formOpen}
        editingServer={editingServer}
        onClose={handleFormClose}
        onSaved={loadServers}
      />

      <DeleteConfirmDialog
        open={deleteTarget !== null}
        serverName={deleteTarget?.name ?? ""}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
      />
    </div>
  );
}


interface AuthConfigCardProps {
  config: any;
  onEdit: () => void;
  onDelete: () => void;
  onRefreshJwks: () => void;
  actionLoading: boolean;
}

function AuthConfigCard({
  config,
  onEdit,
  onDelete,
  onRefreshJwks,
  actionLoading,
}: AuthConfigCardProps) {
  const { toast } = useToast();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = async (text: string, field: string) => {
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
      toast({ title: "Copied to clipboard" });
    }
  };

  const mode = config.effectiveMode || config.authMode || "none";
  const isOAuth2 = mode === "oauth2";
  const source = config.configSource || "default";
  const idpName = config.idpDisplayName || detectIdpType(config.issuerUri);
  const isDbSource = source === "database";

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm max-w-3xl overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <h3 className="text-2xl font-extrabold text-foreground tracking-tight">{idpName}</h3>
          <span className={cn(
            "inline-flex items-center gap-1.5 text-[11px] font-medium",
            isOAuth2 ? "text-emerald-400" : "text-red-400"
          )}>
            <span className={cn("w-2 h-2 rounded-full", isOAuth2 ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)]" : "bg-red-400")} />
            {isOAuth2 ? "Active" : "Disabled"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {isDbSource ? (
            <>
              <Button variant="outline" size="sm" onClick={onEdit} disabled={actionLoading} className="border-border text-foreground/80 hover:text-foreground h-8 w-8 p-0" title="Edit configuration">
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              <Button variant="outline" size="sm" onClick={onRefreshJwks} disabled={actionLoading} className="border-primary/40 text-primary hover:bg-primary/10 h-8 w-8 p-0" title="Refresh JWKS">
                {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </Button>
              <Button variant="outline" size="sm" onClick={onDelete} disabled={actionLoading} className="border-red-800/30 text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" title="Delete configuration">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          ) : (
            <Button variant="outline" size="sm" onClick={onEdit} disabled={actionLoading} className="border-primary/40 text-primary hover:bg-primary/10 h-8 w-8 p-0" title="Save to database">
              <Settings className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 p-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Source</span>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium text-muted-foreground border border-border bg-muted/60" title="Configuration source">
            {isDbSource ? (
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="20" height="14" x="2" y="3" rx="2" /><line x1="8" x2="16" y1="21" y2="21" /><line x1="12" x2="12" y1="17" y2="21" />
              </svg>
            )}
              {isDbSource ? "Database" : "Environment"}
            </div>
          </div>

          {[
            { label: "Mode", value: mode.toUpperCase() },
            { label: "Classification", value: (config.tokenClassificationMode || "strict").toUpperCase() },
            { label: "Grace Period", value: `${config.gracePeriodMinutes || 30} min` },
            ...(config.createdAt ? [{ label: "Created", value: formatTimestamp(config.createdAt) }] : []),
          ].map((stat) => (
            <div key={stat.label} className="flex items-center justify-between rounded-lg border border-border bg-muted/60 px-4 py-3">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</span>
              <span className="text-sm font-bold text-foreground">{stat.value}</span>
            </div>
          ))}

          {config.supportedScopes && config.supportedScopes.length > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Scopes</span>
              <div className="flex flex-wrap gap-1.5">
                {(Array.isArray(config.supportedScopes) ? config.supportedScopes : String(config.supportedScopes).split(",").map((s: string) => s.trim()).filter(Boolean)).map((scope: string) => (
                  <span key={scope} className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary/10 text-primary border border-primary/20">
                    {scope}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="xl:col-span-2 flex flex-col gap-4">
          <div className="rounded-xl border border-border bg-muted/60 p-5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-5">
              Identity Provider
            </p>

            {config.issuerUri && (
              <div className="mb-4">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">Issuer URI</p>
                <div className="flex items-start gap-2 font-mono text-xs text-foreground/70 bg-muted/60 rounded-lg px-4 py-3 border border-border">
                  <span className="flex-1 break-all leading-6">{config.issuerUri}</span>
                  <button onClick={() => handleCopy(config.issuerUri, "issuer")} className="text-muted-foreground/50 hover:text-foreground flex-shrink-0 transition-colors mt-0.5" title="Copy">
                    {copiedField === "issuer" ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            )}

            <div className="mb-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">JWKS URI</p>
              <div className="flex items-start gap-2 font-mono text-xs text-foreground/70 bg-muted/60 rounded-lg px-4 py-3 border border-border">
                <span className="flex-1 break-all leading-6">{config.jwksUri || "auto-discovered"}</span>
                {config.jwksUri && (
                  <button onClick={() => handleCopy(config.jwksUri, "jwks")} className="text-muted-foreground/50 hover:text-foreground flex-shrink-0 transition-colors mt-0.5" title="Copy">
                    {copiedField === "jwks" ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {config.jwtDecoderActive && (
                <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1.5">
                  <CheckCircle className="w-3 h-3" /> JWT Decoder active
                </span>
              )}
              {config.gracePeriodActive && (
                <span className="inline-flex items-center gap-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1.5">
                  <Clock className="w-3 h-3" /> Grace period — {config.gracePeriodRemainingMinutes ?? "?"} min
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


interface AuthConfigFormProps {
  existing: any | null;
  onSaved: () => void;
  onCancel: () => void;
}

function AuthConfigForm({ existing, onSaved, onCancel }: AuthConfigFormProps) {
  const { toast } = useToast();
  const isEdit = existing !== null;

  const [form, setForm] = useState<AuthFormState>(() => {
    if (existing) {
      return {
        idpDisplayName: existing.idpDisplayName ?? "",
        authMode: existing.authMode ?? "oauth2",
        issuerUri: existing.issuerUri ?? "",
        jwksUri: existing.jwksUri ?? "",
        authorizationEndpoint: existing.authorizationEndpoint ?? "",
        tokenEndpoint: existing.tokenEndpoint ?? "",
        tokenClassificationMode: existing.tokenClassificationMode ?? "STRICT",
        gracePeriodMinutes: String(existing.gracePeriodMinutes ?? "30"),
      };
    }
    return EMPTY_AUTH_FORM;
  });

  const [saving, setSaving] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [validation, setValidation] = useState<ValidationResult>({ state: "idle" });

  const setField = (key: keyof AuthFormState, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleIssuerChange = (val: string) => {
    setForm((f) => ({
      ...f,
      issuerUri: val,
      jwksUri: "",
      authorizationEndpoint: "",
      tokenEndpoint: "",
    }));
    setValidation({ state: "idle" });
  };

  const handleDiscover = async () => {
    if (!form.issuerUri.trim()) {
      toast({ title: "Enter an Issuer URI first", variant: "destructive" });
      return;
    }
    setDiscovering(true);
    try {
      const result = await GatewayAuthConfigApi.discover({ issuerUri: form.issuerUri.trim() });
      if (result.error) {
        toast({
          title: "Discovery failed",
          description: result.error,
          variant: "destructive",
        });
        return;
      }
      setForm((f) => ({
        ...f,
        jwksUri: result.jwksUri ?? f.jwksUri,
        authorizationEndpoint: result.authorizationEndpoint ?? f.authorizationEndpoint,
        tokenEndpoint: result.tokenEndpoint ?? f.tokenEndpoint,
      }));
      toast({ title: "Endpoints discovered successfully" });
    } catch (e: any) {
      toast({
        title: "Discovery failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setDiscovering(false);
    }
  };

  const handleValidate = async () => {
    if (!form.issuerUri.trim()) {
      toast({ title: "Enter an Issuer URI first", variant: "destructive" });
      return;
    }
    setValidation({ state: "loading" });
    try {
      const result = await GatewayAuthConfigApi.validate({
        issuerUri: form.issuerUri.trim(),
        jwksUri: form.jwksUri.trim() || undefined,
      });
      if (result.jwksReachable) {
        setValidation({
          state: "success",
          message: `${result.jwksKeyCount} keys found`,
          jwksKeyCount: result.jwksKeyCount,
          jwksLatencyMs: result.jwksLatencyMs,
        });
      } else {
        setValidation({
          state: "error",
          message: result.errorMessage || "Validation failed",
        });
      }
    } catch (e: any) {
      setValidation({ state: "error", message: apiError(e) });
    }
  };

  const handleSave = async () => {
    if (!form.issuerUri.trim() && form.authMode === "oauth2") {
      toast({ title: "Issuer URI is required for OAuth2 mode", variant: "destructive" });
      return;
    }
    const body = {
      authMode: form.authMode,
      idpDisplayName: form.idpDisplayName.trim() || undefined,
      issuerUri: form.issuerUri.trim() || undefined,
      jwksUri: form.jwksUri.trim() || undefined,
      authorizationEndpoint: form.authorizationEndpoint.trim() || undefined,
      tokenEndpoint: form.tokenEndpoint.trim() || undefined,
      tokenClassificationMode: form.tokenClassificationMode,
      gracePeriodMinutes: parseInt(form.gracePeriodMinutes, 10),
      enabled: true,
    };
    setSaving(true);
    try {
      if (isEdit && existing?.id) {
        await GatewayAuthConfigApi.update(body);
        toast({ title: "Authentication config updated" });
      } else {
        await GatewayAuthConfigApi.create(body);
        toast({ title: "Authentication config created" });
      }
      onSaved();
    } catch (e: any) {
      toast({
        title: "Save failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-base font-semibold text-foreground">Configure Authentication</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Connect an Identity Provider to enable OAuth2 for AI agents.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="border-border text-muted-foreground hover:text-foreground"
          >
            Discard
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEdit ? "Update Configuration" : "Save Configuration"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 flex flex-col gap-5 rounded-xl border border-border bg-card p-6">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Globe className="w-3.5 h-3.5 text-primary" />
            Identity Provider
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-xs text-muted-foreground uppercase tracking-wider">
                IDP Display Name
              </label>
              <input
                type="text"
                value={form.idpDisplayName}
                onChange={(e) => setField("idpDisplayName", e.target.value)}
                placeholder="e.g. Corporate Okta"
                className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-muted-foreground uppercase tracking-wider">
                Auth Mode <span className="text-red-400">*</span>
              </label>
              <Select value={form.authMode} onValueChange={(v) => setField("authMode", v)}>
                <SelectTrigger className="bg-muted border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  <SelectItem value="oauth2">OIDC / OAuth2.0</SelectItem>
                  <SelectItem value="none">None (disabled)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-muted-foreground uppercase tracking-wider">
              Issuer URI <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.issuerUri}
              onChange={(e) => handleIssuerChange(e.target.value)}
              placeholder="https://auth.company.com"
              className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50"
            />
            <div className="flex gap-2 mt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDiscover}
                disabled={discovering || !form.issuerUri.trim()}
                className="border-primary/40 text-primary hover:bg-primary/10 hover:text-primary whitespace-nowrap"
              >
                {discovering ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Search className="w-3.5 h-3.5 mr-1.5" />
                )}
                Discover
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleValidate}
                disabled={validation.state === "loading" || !form.issuerUri.trim()}
                className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-400 whitespace-nowrap"
              >
                {validation.state === "loading" ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                )}
                Validate
              </Button>
            </div>

            {validation.state === "success" && (
              <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2 mt-1">
                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                JWKS reachable &mdash; {validation.jwksKeyCount} keys found &mdash;{" "}
                {validation.jwksLatencyMs}ms latency
              </div>
            )}
            {validation.state === "error" && (
              <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                {validation.message}
              </div>
            )}

            <p className="text-xs text-amber-500/80 flex items-start gap-2 leading-5 mt-1">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Issuer URI must match the Frontend URL in your Identity Provider (e.g. Keycloak&rsquo;s Frontend URL, Azure AD&rsquo;s Issuer URI).</span>
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-xs text-muted-foreground uppercase tracking-wider">JWKS URI</label>
            <input
              type="text"
              value={form.jwksUri}
              onChange={(e) => setField("jwksUri", e.target.value)}
              placeholder="https://auth.company.com/.well-known/jwks.json"
              className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50"
            />
          </div>

          <div className="flex flex-col gap-4 border-t border-border pt-5 mt-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              OAuth2 Endpoints
            </p>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-muted-foreground uppercase tracking-wider">
                Authorization Endpoint
              </label>
              <input
                type="text"
                value={form.authorizationEndpoint}
                onChange={(e) => setField("authorizationEndpoint", e.target.value)}
                placeholder="https://auth.company.com/v1/authorize"
                className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-xs text-muted-foreground uppercase tracking-wider">
                Token Endpoint
              </label>
              <input
                type="text"
                value={form.tokenEndpoint}
                onChange={(e) => setField("tokenEndpoint", e.target.value)}
                placeholder="https://auth.company.com/v1/token"
                className="w-full bg-muted border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary/50"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 flex flex-col gap-5 rounded-xl border border-border bg-card p-6">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
            <Settings className="w-3.5 h-3.5 text-primary" />
            Execution Policy
          </p>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-muted-foreground uppercase tracking-wider">
              Token Classification
            </label>
            <Select
              value={form.tokenClassificationMode}
              onValueChange={(v) => setField("tokenClassificationMode", v)}
            >
              <SelectTrigger className="bg-muted border-border text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border">
                {TOKEN_CLASSIFICATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground/70">
              {
                TOKEN_CLASSIFICATION_OPTIONS.find(
                  (o) => o.value === form.tokenClassificationMode
                )?.description
              }
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-muted-foreground uppercase tracking-wider">
              Grace Period
            </label>
            <Select
              value={form.gracePeriodMinutes}
              onValueChange={(v) => setField("gracePeriodMinutes", v)}
            >
              <SelectTrigger className="bg-muted border-border text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-muted border-border">
                <SelectItem value="5">5 minutes</SelectItem>
                <SelectItem value="15">15 minutes</SelectItem>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="480">8 hours</SelectItem>
                <SelectItem value="1440">24 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/60 rounded-lg mt-auto">
            <span className="text-xs text-muted-foreground">Policy Health Check</span>
            <Badge
              variant="outline"
              className="border-emerald-500/30 text-emerald-400 bg-emerald-500/10 text-xs"
            >
              ACTIVE
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
}


function AuthenticationTab() {
  const { toast } = useToast();
  const [authConfig, setAuthConfig] = useState<any | null>(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editExisting, setEditExisting] = useState<any | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadAuthConfig = useCallback(async () => {
    setLoadingAuth(true);
    try {
      const result = await GatewayAuthConfigApi.get();
      setAuthConfig(result);
    } catch (e: any) {
      if (e?.status === 404 || e?.response?.status === 404) {
        setAuthConfig(null);
      } else {
        toast({
          title: "Failed to load auth config",
          description: apiError(e),
          variant: "destructive",
        });
      }
    } finally {
      setLoadingAuth(false);
    }
  }, [toast]);

  useEffect(() => {
    loadAuthConfig();
  }, [loadAuthConfig]);

  const isOAuth2 = authConfig && (authConfig.effectiveMode === "oauth2" || authConfig.authMode === "oauth2");
  const isConfigured = authConfig && (isOAuth2 || authConfig.configSource === "database");

  const handleEdit = async () => {
    try {
      const config = await GatewayAuthConfigApi.get();
      setEditExisting(config);
      setShowForm(true);
    } catch (e: any) {
      toast({
        title: "Failed to load config for editing",
        description: apiError(e),
        variant: "destructive",
      });
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await GatewayAuthConfigApi.remove();
      toast({ title: "Auth config deleted — reverted to defaults" });
      setDeleteDialogOpen(false);
      await loadAuthConfig();
    } catch (e: any) {
      toast({
        title: "Delete failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleRefreshJwks = async () => {
    setActionLoading(true);
    try {
      await GatewayAuthConfigApi.refreshJwks();
      toast({ title: "JWKS keys refreshed" });
    } catch (e: any) {
      toast({
        title: "JWKS refresh failed",
        description: apiError(e),
        variant: "destructive",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleFormSaved = async () => {
    setShowForm(false);
    setEditExisting(null);
    await loadAuthConfig();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditExisting(null);
  };

  if (loadingAuth) {
    return <Skeleton className="h-64 w-full max-w-lg rounded-xl" />;
  }

  if (showForm) {
    return (
      <AuthConfigForm
        existing={editExisting}
        onSaved={handleFormSaved}
        onCancel={handleFormCancel}
      />
    );
  }

  if (!isConfigured) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-muted/50 border border-border flex items-center justify-center">
          <ShieldCheck className="w-8 h-8 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-foreground">Authentication Not Configured</h3>
          <p className="text-sm text-muted-foreground max-w-md mt-1">
            Configure an Identity Provider (Keycloak, Azure AD, Okta, Auth0) to enable OAuth2
            authentication for AI agents connecting to the gateway.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditExisting(null);
            setShowForm(true);
          }}
          className="mt-2 bg-primary hover:bg-primary text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Configure Identity Provider
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <AuthConfigCard
        config={authConfig}
        onEdit={handleEdit}
        onDelete={() => setDeleteDialogOpen(true)}
        onRefreshJwks={handleRefreshJwks}
        actionLoading={actionLoading}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={(v) => !v && setDeleteDialogOpen(false)}>
        <DialogContent className="max-w-sm bg-card border-border text-foreground">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Delete Auth Config
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Delete auth config? This will revert to environment variables or mode=none.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              className="text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-700 hover:bg-red-600 text-white"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default function GatewayConfigPage() {
  return (
    <div className="flex flex-col gap-6 p-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <Settings className="w-5 h-5 text-primary" />
          Configuration
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Manage MCP server connections and authentication settings
        </p>
      </div>

      <Tabs defaultValue="servers">
        <TabsList className="bg-muted/50 border border-border">
          <TabsTrigger
            value="servers"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground"
          >
            <Server className="w-4 h-4 mr-2" />
            MCP Servers
          </TabsTrigger>
          <TabsTrigger
            value="auth"
            className="data-[state=active]:bg-primary/15 data-[state=active]:text-primary data-[state=active]:shadow-sm text-muted-foreground"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            Authentication
          </TabsTrigger>
        </TabsList>

        <TabsContent value="servers" className="mt-6">
          <McpServersTab />
        </TabsContent>

        <TabsContent value="auth" className="mt-6">
          <AuthenticationTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
